import React, {useState} from 'react';
import {Field, Form, Formik, FormikValues} from "formik";
import * as Yup from "yup";
import TextFieldWithErrorMessage from "../form/TextFieldWithErrorMessage";
import ComboboxWithErrorMessage from "../form/ComboboxWithErrorMessage";
import {useTranslation} from "react-i18next";
import {RR_STATUS} from "../../constants/RRConstants";
import {useSnackbar} from "notistack";
import Loader from "../core/Loader";
import {addMilestone, getAdditionalMilestoneData, getMilestoneTypesAndContainerData} from "../../api/MilestoneEndpoint";
import DatePickerWithErrorMessage from "../form/DatePickerWithErrorMessage";
import CustomCheckbox from "../form/CustomCheckbox";
import {EVENT_UPDATE_PAGE_PATH} from "../../layouts/navigation/Navigator";
import queryString from 'query-string';
import {useNavigate, useLocation} from "react-router-dom";
import moment from "moment";
import cloneDeep from 'lodash/cloneDeep';
import { normalizeToString } from '../../utils/QueryParamNormalizerUtils';


const validationSchema = {
    container: Yup.string(),
    milestoneType: Yup.string()
        .required("form.event.error.milestone-type-required", true),
    location: Yup.string()
        .required("form.event.error.location-required", true),
    newLocation: Yup.string()
        .when("location", {
            is: (val) => val?.includes("Other..."),
            then: Yup.string()
                .max(100, "form.event.error.max-length")
                .required("form.event.error.new-location-required", true)
        }),
    milestoneDate: Yup.lazy(() => Yup.date()
        .when("milestoneType", {
            is: (val) => isRealMilestoneType(val),
            then: Yup.date()
                .max(moment(), "form.event.error.date-not-in-future")
        })
        .typeError("form.event.error.date-invalid")
        .required("form.event.error.date-invalid")
    ),
    notifyParties: Yup.boolean(),
    remark: Yup.string().max(1000, "form.event.error.remark-max-length")
        .when("notifyParties", {
            is: true,
            then: Yup.string()
                .required("form.event.error.remark-required", true)
        })
};

const addFieldToValidationSchema = (schema: any, field: ISpecialField): any => {
    if (field) {
        let validator = Yup.string().max(100, "form.event.error.max-length");

        if (field.required) {
            validator = validator.required("form.event.error.field-required", true);
        }

        schema[field.key] = validator;
    }
    return schema;
};

const isRealMilestoneType = (milestoneType: string): boolean => {
    if (milestoneType) {
        return !!parseStringToType<IMilestoneType>(milestoneType)?.real
    }
    return false;
};

interface IMilestoneType {
    id: number
    name: string
    real: boolean
}

interface IContainer {
    id: number
    containerNumber: string
    sealNumber: string
    atbNumber: string
}

interface ILocation {
    id: number
    routingPointId: number
    name: string
    newLocation: boolean
    unknownLocation: boolean
}

interface ISpecialField {
    key: string
    required: boolean
}

type SetFieldValueFn = (field: string, value: any) => void

const filterContainersWithContainerNumber = (containers: IContainer[]): IContainer[] | undefined => {
    return containers.filter(container =>
        (container.containerNumber !== null && container.containerNumber !== undefined)
    );
};

const parseStringToType = <T, >(stringToParse: string): T | null => {
    return stringToParse.length > 0 ? JSON.parse(stringToParse) : null;
};

type EventFormProps = {
    shipmentId: number | string | undefined
    formRef: any
}

const EventForm: React.FC<EventFormProps> = ({shipmentId, formRef}) => {

    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const {redirect} = queryString.parse(location.search);

    const [milestoneTypes, setMilestoneTypes] = useState<IMilestoneType[] | undefined>(undefined);
    const [containers, setContainers] = useState<IContainer[] | undefined>(undefined);
    const [locations, setLocations] = useState<ILocation[] | undefined>(undefined);
    const [specialFields, setSpecialFields] = useState<ISpecialField[] | undefined>(undefined);
    const [isNewLocation, setIsNewLocation] = useState<boolean>(false);
    const [dynamicValidationSchema, setDynamicValidationSchema] = useState(Yup.object().shape(validationSchema));

    React.useEffect(() => {
        getMilestoneTypesAndContainerData(shipmentId).then(({data}) => {
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setMilestoneTypes(response.milestoneTypes);
                setContainers(filterContainersWithContainerNumber(response.containers));
            }

        })
    }, [enqueueSnackbar, shipmentId, t]);

    const initializeSpecialFields = (specialFields: ISpecialField[], setFieldValue: SetFieldValueFn) => {
        specialFields?.forEach(field => setFieldValue(field.key, ""))
    };

    const updateSchema = (specialFields: ISpecialField[]) => {
        const schema = specialFields ? specialFields.reduce(addFieldToValidationSchema, cloneDeep(validationSchema)) : validationSchema;
        setDynamicValidationSchema(Yup.object().shape(schema));
    };

    const handleMilestoneTypeChange = (event: React.ChangeEvent<HTMLSelectElement>, setFieldValue: SetFieldValueFn) => {
        const milestoneType: IMilestoneType | null = parseStringToType<IMilestoneType>(event.target.value);
        setFieldValue("location", undefined);

        if (milestoneType) {
            getAdditionalMilestoneData(shipmentId, milestoneType.id).then(({data}) => {
                const {status, response} = data;

                if (status !== RR_STATUS.OK) {
                    enqueueSnackbar(t("core.server-error"), {
                        variant: "error",
                        preventDuplicate: false
                    });
                } else {
                    initializeSpecialFields(response.fields, setFieldValue);
                    updateSchema(response.fields);

                    setLocations(response.locations);
                    setSpecialFields(response.fields);
                }
            });
        }
        setFieldValue("milestoneType", event.target.value); // required, otherwise validation does not work properly
    };

    const handleLocationChange = (event: React.ChangeEvent<HTMLSelectElement>, setFieldValue: SetFieldValueFn) => {
        const location: ILocation | null = parseStringToType<ILocation>(event.target.value);
        const newLocation: boolean = !!location?.newLocation;
        setIsNewLocation(newLocation);

        if (!newLocation) {
            setFieldValue("newLocation", "");
        }

        setFieldValue("location", event.target.value); // required, otherwise validation does not work properly
    };

    const submitForm = (values: FormikValues) => {
        const container: IContainer | null = parseStringToType<IContainer>(values.container);
        const location: ILocation | null = parseStringToType<ILocation>(values.location);
        const milestoneType: IMilestoneType | null = parseStringToType<IMilestoneType>(values.milestoneType);
        const specialFieldValues = {};
        specialFields?.forEach((field, index) => {
            specialFieldValues[index + 1] = values[field.key];
        });

        return addMilestone({
            ...values,
            shipmentId: shipmentId,
            milestoneType: milestoneType?.id,
            locationId: location?.id,
            routingPointId: location?.routingPointId,
            container: container,
            milestoneDate: moment(values.milestoneDate).utc(true),
            additionalAttributes: specialFieldValues
        }).then(({data}) => {
            const {status} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("form.event.error.add-event-failed"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                enqueueSnackbar(t("form.event.submission-success"), {
                    variant: "success",
                    preventDuplicate: false
                });

                navigate(normalizeToString(redirect) ?? EVENT_UPDATE_PAGE_PATH);
            }
        });
    };


    if (!milestoneTypes) {
        return <Loader/>
    }

    return (
        <Formik
            initialValues={{
                container: "",
                milestoneType: "",
                location: "",
                newLocation: "",
                milestoneDate: new Date(),
                remark: "",
                notifyParties: false
            }}
            validationSchema={dynamicValidationSchema}
            onSubmit={submitForm}
            innerRef={formRef}
        >
            {({values, setFieldValue}) => (
                <Form>
                    {!!containers?.length &&
                    <Field component={ComboboxWithErrorMessage} name="container" caption="form.event.container"
                           variant="outlined" size="small">
                        <option key={"null-container"}/>
                        {containers?.map(container => {
                            return <option key={container.id}
                                           value={JSON.stringify(container)}>{container.containerNumber}</option>
                        })}
                    </Field>}

                    <Field component={ComboboxWithErrorMessage} name="milestoneType" caption="form.event.milestone-type"
                           variant="outlined" size="small" required
                           onChange={event => handleMilestoneTypeChange(event, setFieldValue)}>
                        <option key={"null-milestone"}/>
                        {milestoneTypes?.map(milestoneType => {
                            return <option key={milestoneType.id}
                                           value={JSON.stringify(milestoneType)}>{milestoneType.name}</option>
                        })}
                    </Field>

                    {locations &&
                    <Field component={ComboboxWithErrorMessage} name="location" caption="form.event.location"
                           variant="outlined" size="small" required
                           onChange={event => handleLocationChange(event, setFieldValue)}>
                        <option key={"null-location"}/>
                        {locations?.map(location => {
                            return <option key={location.name} value={JSON.stringify(location)}>{location.name}</option>
                        })}
                    </Field>}

                    {isNewLocation && <Field component={TextFieldWithErrorMessage} name="newLocation"
                                             caption="form.event.new-location" variant="outlined" size="small"/>}

                    <Field component={DatePickerWithErrorMessage} name="milestoneDate"
                           inputVariant="outlined" variant="inline" size="small" required={true}/>

                    <Field component={TextFieldWithErrorMessage} name="remark" caption="form.event.remark"
                           variant="outlined" size="small" multiline rows={5} required={values.notifyParties}/>

                    <Field component={CustomCheckbox} name="notifyParties" caption="form.event.notify-parties"
                           size="small" color="primary"/>

                    {
                        specialFields?.map(field => {
                            return <Field component={TextFieldWithErrorMessage} key={field.key} name={field.key}
                                          caption={field.key}
                                          variant="outlined" size="small" required={field.required}/>
                        })
                    }

                </Form>
            )}
        </Formik>
    );
};

export default EventForm;